import { useEffect } from "react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { AppHeader } from "@/components/AppHeader";
import { Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const AppLayout = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Prefetch crypto-assets data so it's ready when user navigates
  useEffect(() => {
    if (user) {
      queryClient.prefetchQuery({
        queryKey: ['crypto-assets'],
        queryFn: async () => {
          const [valuationRes, socialRes, fundamentalsRes, alertsRes, fngRes] = await Promise.allSettled([
            supabase.functions.invoke('valuation-summary'),
            supabase.functions.invoke('social-data'),
            supabase.functions.invoke('fundamentals-data'),
            supabase.from('crypto_alerts').select('*').gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()),
            supabase.functions.invoke('fear-greed-index')
          ]);
          // Return raw results - the actual hook will process them
          return valuationRes.status === 'fulfilled' ? valuationRes.value.data : null;
        },
        staleTime: 300000,
      });
    }
  }, [user, queryClient]);

  // Redirect to auth if not logged in
  useEffect(() => {
    if (!loading && !user) {
      navigate('/auth');
    }
  }, [user, loading, navigate]);

  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // Don't render layout if not authenticated
  if (!user) {
    return null;
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar />
        <div className="flex-1 flex flex-col">
          <AppHeader />
          <main className="flex-1 p-3 md:p-6">
            <Outlet />
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};